/*
 * 0.69: Gesetzliche Feiertage in Deutschland (bundesweit und je Bundesland) und Werktagsrechnung.
 * Berechnet, nicht gespeichert: feste Tage, Ostern (gregorianisch) und davon abhängige Tage, Buß- und Bettag.
 * Nur landesweit geltende Feiertage – solche, die nur in einzelnen Gemeinden gelten (z. B. Mariä Himmelfahrt
 * in Teilen Bayerns, Fronleichnam in Teilen Sachsens und Thüringens), fehlen bewusst.
 */

export const HOLIDAY_REGIONS = [
  ['DE', 'Bundesweit (nur gemeinsame Feiertage)'],
  ['DE-BW', 'Baden-Württemberg'],
  ['DE-BY', 'Bayern'],
  ['DE-BE', 'Berlin'],
  ['DE-BB', 'Brandenburg'],
  ['DE-HB', 'Bremen'],
  ['DE-HH', 'Hamburg'],
  ['DE-HE', 'Hessen'],
  ['DE-MV', 'Mecklenburg-Vorpommern'],
  ['DE-NI', 'Niedersachsen'],
  ['DE-NW', 'Nordrhein-Westfalen'],
  ['DE-RP', 'Rheinland-Pfalz'],
  ['DE-SL', 'Saarland'],
  ['DE-SN', 'Sachsen'],
  ['DE-ST', 'Sachsen-Anhalt'],
  ['DE-SH', 'Schleswig-Holstein'],
  ['DE-TH', 'Thüringen'],
] as const;
export type HolidayRegion = (typeof HOLIDAY_REGIONS)[number][0];
export const HOLIDAY_REGION_NAME = Object.fromEntries(HOLIDAY_REGIONS) as Record<HolidayRegion, string>;
export const isHolidayRegion = (r: unknown): r is HolidayRegion => HOLIDAY_REGIONS.some(([k]) => k === r);

const ymd = (y: number, m: number, d: number) => new Date(Date.UTC(y, m - 1, d)).toISOString().slice(0, 10);

/** Ostersonntag (gregorianisch, anonymer Algorithmus) */
export function easterSunday(y: number): string {
  const a = y % 19;
  const b = Math.floor(y / 100);
  const c = y % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return ymd(y, month, day);
}

const plus = (date: string, n: number) => {
  const [y, m, d] = date.split('-').map(Number);
  return ymd(y!, m!, d! + n);
};

type Rule = { name: string; date: (y: number) => string; regions: 'all' | string[]; from?: number };

const RULES: Rule[] = [
  { name: 'Neujahr', date: (y) => ymd(y, 1, 1), regions: 'all' },
  { name: 'Heilige Drei Könige', date: (y) => ymd(y, 1, 6), regions: ['BW', 'BY', 'ST'] },
  { name: 'Internationaler Frauentag', date: (y) => ymd(y, 3, 8), regions: ['BE'], from: 2019 },
  { name: 'Internationaler Frauentag', date: (y) => ymd(y, 3, 8), regions: ['MV'], from: 2023 },
  { name: 'Karfreitag', date: (y) => plus(easterSunday(y), -2), regions: 'all' },
  { name: 'Ostersonntag', date: (y) => easterSunday(y), regions: ['BB'] },
  { name: 'Ostermontag', date: (y) => plus(easterSunday(y), 1), regions: 'all' },
  { name: 'Tag der Arbeit', date: (y) => ymd(y, 5, 1), regions: 'all' },
  { name: 'Christi Himmelfahrt', date: (y) => plus(easterSunday(y), 39), regions: 'all' },
  { name: 'Pfingstsonntag', date: (y) => plus(easterSunday(y), 49), regions: ['BB'] },
  { name: 'Pfingstmontag', date: (y) => plus(easterSunday(y), 50), regions: 'all' },
  { name: 'Fronleichnam', date: (y) => plus(easterSunday(y), 60), regions: ['BW', 'BY', 'HE', 'NW', 'RP', 'SL'] },
  { name: 'Mariä Himmelfahrt', date: (y) => ymd(y, 8, 15), regions: ['SL'] },
  { name: 'Weltkindertag', date: (y) => ymd(y, 9, 20), regions: ['TH'], from: 2019 },
  { name: 'Tag der Deutschen Einheit', date: (y) => ymd(y, 10, 3), regions: 'all' },
  { name: 'Reformationstag', date: (y) => ymd(y, 10, 31), regions: ['BB', 'HB', 'HH', 'MV', 'NI', 'SN', 'ST', 'SH', 'TH'] },
  { name: 'Allerheiligen', date: (y) => ymd(y, 11, 1), regions: ['BW', 'BY', 'NW', 'RP', 'SL'] },
  {
    name: 'Buß- und Bettag',
    // Mittwoch vor dem 23. November
    date: (y) => {
      const nov23 = new Date(Date.UTC(y, 10, 23));
      const back = ((nov23.getUTCDay() - 3 + 7) % 7) || 7;
      return ymd(y, 11, 23 - back);
    },
    regions: ['SN'],
  },
  { name: '1. Weihnachtstag', date: (y) => ymd(y, 12, 25), regions: 'all' },
  { name: '2. Weihnachtstag', date: (y) => ymd(y, 12, 26), regions: 'all' },
];

const cache = new Map<string, Map<string, string>>();

/** Feiertage eines Jahres: Datum → Name */
export function holidaysOf(year: number, region: HolidayRegion): Map<string, string> {
  const key = `${year}|${region}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const land = region === 'DE' ? null : region.slice(3);
  const out = new Map<string, string>();
  for (const r of RULES) {
    if (r.from && year < r.from) continue;
    if (r.regions !== 'all' && (!land || !r.regions.includes(land))) continue;
    out.set(r.date(year), r.name);
  }
  if (cache.size > 200) cache.clear();
  cache.set(key, out);
  return out;
}

/** Name des Feiertags an diesem Tag oder null */
export function holidayName(date: string, region: HolidayRegion | null | undefined): string | null {
  if (!region) return null;
  return holidaysOf(Number(date.slice(0, 4)), region).get(date) ?? null;
}

export interface WorkdayRule {
  /** Samstag und Sonntag sind keine Arbeitstage */
  weekends: boolean;
  /** Feiertage dieser Region sind keine Arbeitstage (null = keine Feiertage) */
  holidays: HolidayRegion | null;
}

const weekday = (date: string) => {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d!)).getUTCDay(); // 0 = Sonntag
};

/** Grund, warum ein Tag kein Arbeitstag ist („Samstag“, „Pfingstmontag“) – null = Arbeitstag */
export function nonWorkingReason(date: string, rule: WorkdayRule): string | null {
  if (rule.weekends) {
    const wd = weekday(date);
    if (wd === 6) return 'Samstag';
    if (wd === 0) return 'Sonntag';
  }
  return holidayName(date, rule.holidays);
}

/** Nächster (dir = 1) bzw. vorheriger (dir = -1) Arbeitstag ab einschließlich `date` */
export function nearestWorkday(date: string, rule: WorkdayRule, dir: 1 | -1): string {
  let d = date;
  for (let i = 0; i < 31 && nonWorkingReason(d, rule); i++) d = plus(d, dir);
  return d;
}

/** Datum + n Arbeitstage (n > 0 vorwärts, n < 0 rückwärts; 0 = derselbe Tag bzw. nächster Arbeitstag) */
export function addWorkdays(date: string, n: number, rule: WorkdayRule): string {
  if (n === 0) return nearestWorkday(date, rule, 1);
  const dir = n > 0 ? 1 : -1;
  let d = date;
  for (let left = Math.abs(n); left > 0; ) {
    d = plus(d, dir);
    if (!nonWorkingReason(d, rule)) left--;
  }
  return d;
}
